import React, { useState, useEffect } from 'react';
import { imageCache } from '../utils/imageCache';
import { LoadingSpinner } from './LoadingSpinner';

interface CachedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  fallback?: React.ReactNode;
  showLoader?: boolean;
}

export const CachedImage: React.FC<CachedImageProps> = ({ 
  src, 
  alt, 
  fallback, 
  showLoader = true,
  className = '',
  ...props 
}) => {
  const [cachedSrc, setCachedSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const loadCachedImage = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        
        const cached = await imageCache.get(src);
        
        if (!isCancelled) {
          setCachedSrc(cached);
          setIsLoading(false);
        }
      } catch (error) {
        console.warn('Image cache failed:', src, error);
        if (!isCancelled) {
          setCachedSrc(src); // Fallback to original
          setIsLoading(false);
          setHasError(true);
        }
      }
    };

    if (src) {
      loadCachedImage();
    }

    return () => {
      isCancelled = true;
    };
  }, [src]);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  if (hasError && fallback) {
    return <>{fallback}</>;
  }

  return (
    <div className="relative">
      {isLoading && showLoader && (
        <div className="absolute inset-0 flex items-center justify-center bg-elevated">
          <LoadingSpinner size="sm" />
        </div>
      )}
      
      {cachedSrc && (
        <img
          {...props}
          src={cachedSrc}
          alt={alt}
          className={className}
          onLoad={handleImageLoad}
          onError={handleImageError}
          style={{
            ...props.style,
            opacity: isLoading ? 0.7 : 1,
            transition: 'opacity 0.2s ease-in-out'
          }}
        />
      )}
    </div>
  );
};